import { useState } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * Combined login component supporting Google Sign-In and email/password.
 */
export function GoogleSignInButton() {
  const { signInWithGoogle, signInWithEmail, error } = useAuth();
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);

  const handleGoogleClick = async () => {
    setIsGoogleLoading(true);
    try {
      await signInWithGoogle();
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setIsEmailLoading(true);
    try {
      await signInWithEmail(email, password);
    } finally {
      setIsEmailLoading(false);
    }
  };

  const isLoading = isGoogleLoading || isEmailLoading;

  return (
    <div className="flex flex-col items-center gap-4 w-full max-w-sm">
      {/* Google Sign-In Button */}
      <Button
        variant="outline"
        size="lg"
        className="h-12 w-full gap-3 border-border bg-white text-foreground shadow-sm hover:bg-gray-50 hover:shadow-md transition-all"
        onClick={handleGoogleClick}
        disabled={isLoading}
      >
        {isGoogleLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : (
          <svg
            className="h-5 w-5"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              fill="#4285F4"
            />
            <path
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              fill="#34A853"
            />
            <path
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              fill="#FBBC05"
            />
            <path
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              fill="#EA4335"
            />
          </svg>
        )}
        <span className="font-medium">
          {isGoogleLoading ? 'Signing in...' : 'Sign in with Google'}
        </span>
      </Button>

      {/* Divider */}
      <div className="relative w-full">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-background px-2 text-muted-foreground">
            Or continue with email
          </span>
        </div>
      </div>

      {/* Email/Password Form */}
      {!showEmailForm ? (
        <Button
          variant="outline"
          size="lg"
          className="h-12 w-full gap-3"
          onClick={() => setShowEmailForm(true)}
          disabled={isLoading}
        >
          <Mail className="h-5 w-5" />
          <span className="font-medium">Sign in with Email</span>
        </Button>
      ) : (
        <form onSubmit={handleEmailSubmit} className="w-full space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoComplete="email"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              autoComplete="current-password"
            />
          </div>
          <Button
            type="submit"
            size="lg"
            className="h-12 w-full"
            disabled={isLoading || !email || !password}
          >
            {isEmailLoading ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : null}
            {isEmailLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
      )}

      {error && (
        <p className="text-sm text-destructive text-center w-full">
          {error}
        </p>
      )}
    </div>
  );
}
